import React from 'react'
import { Tooltip } from '@mui/material'
import { AddRounded, DeleteOutline, BlockRounded } from '@mui/icons-material'
import { useDispatch } from '../../store'
import { api } from '../../function/axios'
import { showSnackbar } from '../../store/common/actionCreator'
import { ConfirmV2 } from '../ConfirmV2'
import sprite_ from '../../sass/sprite.module.sass'
import { SourceIcon } from '../source-icon/SourceIcon'
import s from './SystemV2.module.css'

/*
 * System restrictions — which search sources a restriction type turns off.
 * The classic tab's calls: GET /admin_settings/restrictions, PUT
 * /admin_settings/restriction (one source on / off), POST
 * /admin_settings/restriction_create, DELETE /admin_settings/restriction.
 * New here: the list reloads after a type is added (the classic tab needed
 * a page reload), a switch goes back if the server refuses, and deleting a
 * type asks first.
 */

type Source = { source: string; status: boolean }
type Restriction = { type: string; sources: Source[] }

// the types the backend knows (the classic dialog offers just this one)
const TYPES: Record<string, string> = { combined_phone: 'Phone / Advanced phone' }
const typeLabel = (t: string) => TYPES[t] || t.replace(/_/g, ' ')

export function RestrictionsV2() {
  const dispatch = useDispatch()
  const [list, setList] = React.useState<Restriction[] | null>(null)
  const [toDelete, setToDelete] = React.useState<string | null>(null)
  const [busy, setBusy] = React.useState(false)

  const load = React.useCallback(async () => {
    try {
      const { data } = await api({ method: 'GET', url: '/admin_settings/restrictions' })
      setList(Array.isArray(data) ? data : [])
    } catch {
      setList([])
    }
  }, [])
  React.useEffect(() => { void load() }, [load])

  const setSource = (type: string, source: string, status: boolean) =>
    setList((l) => (l ? l.map((r) => (r.type !== type ? r : { ...r, sources: r.sources.map((x) => (x.source === source ? { ...x, status } : x)) })) : l))

  async function toggle(type: string, source: string, value: boolean) {
    setSource(type, source, value)
    try {
      await api({ method: 'PUT', url: '/admin_settings/restriction', body: { type, source, value } })
    } catch {
      setSource(type, source, !value)
      dispatch(showSnackbar('error', `Could not change ${source}`))
    }
  }

  async function add(type: string) {
    setBusy(true)
    try {
      await api({ method: 'POST', url: '/admin_settings/restriction_create', body: { restrictionType: type } })
      await load()
    } catch {
      dispatch(showSnackbar('error', 'Could not add the restriction'))
    } finally {
      setBusy(false)
    }
  }

  async function remove(type: string) {
    try {
      await api({ method: 'DELETE', url: `/admin_settings/restriction?type=${encodeURIComponent(type)}` })
      setList((l) => (l ? l.filter((r) => r.type !== type) : l))
    } catch {
      dispatch(showSnackbar('error', 'Could not delete the restriction'))
    }
  }

  const missing = Object.keys(TYPES).filter((t) => !(list || []).some((r) => r.type === t))

  return (
    <div className={s.section}>
      <div className={s.sectionHead}>
        <div>
          <b>System restrictions</b>
          <span>Turn search sources off for everyone — a source switched off is not searched.</span>
        </div>
        {missing.length > 0 && (
          <button type="button" className={s.primary} onClick={() => { void add(missing[0]) }} disabled={busy}>
            <AddRounded sx={{ fontSize: 17 }} />Add {typeLabel(missing[0])}
          </button>
        )}
      </div>

      {list === null && <div className={s.empty}>Loading…</div>}
      {list?.length === 0 && (
        <div className={s.empty}>
          <BlockRounded sx={{ fontSize: 26 }} />
          <b>No restrictions</b>
          <span>Every source is searched. Add a restriction type to switch sources off.</span>
        </div>
      )}

      {list?.map((r) => {
        const on = r.sources.filter((x) => x.status).length
        return (
          <div key={r.type} className={s.card}>
            <div className={s.cardHead}>
              <b>{typeLabel(r.type)}</b>
              <span className={s.muted}>{on} of {r.sources.length} on</span>
              <span className={s.spacer} />
              <Tooltip title="Delete this restriction type">
                <button type="button" className={s.iconDanger} onClick={() => setToDelete(r.type)} aria-label="Delete"><DeleteOutline sx={{ fontSize: 17 }} /></button>
              </Tooltip>
            </div>
            <div className={s.sources}>
              {r.sources.map((x) => (
                <label key={x.source} className={`${s.source} ${x.status ? s.sourceOn : ''}`}>
                  <SourceIcon source={x.source} className={s.sourceIcon} fallback={<span className={`${s.sourceIcon} ${sprite_[`social__small_${x.source}`] || ''}`} />} />
                  <span className={s.sourceName}>{x.source}</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={x.status}
                    aria-label={x.source}
                    className={`${s.switch} ${x.status ? s.switchOn : ''}`}
                    onClick={() => { void toggle(r.type, x.source, !x.status) }}
                  >
                    <span />
                  </button>
                </label>
              ))}
            </div>
          </div>
        )
      })}

      {toDelete && (
        <ConfirmV2
          header={`Delete the “${typeLabel(toDelete)}” restriction? Its sources are searched again.`}
          onYes={() => { void remove(toDelete) }}
          onClose={() => setToDelete(null)}
        />
      )}
    </div>
  )
}
